import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, CheckCircle, Scales, HandHeart, GraduationCap, Lock } from "@phosphor-icons/react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import RepExample from "@/components/RepExample";
import PageMeta from "@/components/PageMeta";
import { SITE, has } from "@/config/site";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Landing() {
  const lending = SITE.LENDING_ENABLED;

  return (
    <div className="min-h-screen bg-[#F9F7F4] grain">
      <PageMeta
        title="Ascentremit — School fees in monthly instalments"
        description="Pay school fees in monthly instalments or in full. Ascentremit pays the school upfront, and you see the full cost before you start."
      />
      <PublicNav />

      {lending ? <Hero /> : <HeroWaitlist />}

      {lending && <RepExample />}

      <WhatWeDo />
      <HowItWorks />

      <Footer />
    </div>
  );
}

/* ───────────── Hero (lending enabled) ───────────── */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-[#132A13]">
            Pay school fees monthly.{" "}
            <span className="italic text-[#0A3622]">Your child starts term on time.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#4F5D4F] max-w-2xl leading-relaxed">
            Spread fees over {SITE.TENORS_MONTHS[0]} to {SITE.TENORS_MONTHS[SITE.TENORS_MONTHS.length - 1]} months.
            We pay the school in full on day one, and you see the full cost before you start.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 sm:gap-4">
            <Link to="/parent/academic-advance" className="btn-primary" data-testid="hero-cta-borrow">
              Check what you can borrow
              <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/pricing" className="btn-outline" data-testid="hero-cta-costs">
              See the costs
            </Link>
          </div>
          {SITE.SOFT_CHECK_AVAILABLE && (
            <p className="mt-4 text-xs text-[#4F5D4F]">
              <CheckCircle size={14} weight="fill" className="inline text-[#0A3622] mr-1" />
              Soft check — no effect on your credit record.
            </p>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-3xl border border-[#E5E3DB] bg-white p-6 sm:p-8 shadow-sm">
            <div className="overline mb-3">Why parents choose us</div>
            <ul className="space-y-4 text-sm text-[#132A13]">
              <Promise text="The price is on the page. See your rate, monthly payment, total and late fee before you sign." />
              <Promise text="We lend only what you can repay — we'll suggest a smaller plan rather than set you up to fail." />
              <Promise text="Your child is never involved. The school is already paid in full." />
              {!SITE.EARLY_REPAYMENT_PENALTY && (
                <Promise text="Pay off early with no penalty — you pay interest only for the months you borrow." />
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Promise({ text }) {
  return (
    <li className="flex gap-3">
      <Check size={18} weight="bold" className="text-[#0A3622] mt-0.5 shrink-0" />
      <span className="leading-relaxed">{text}</span>
    </li>
  );
}

/* ───────────── Hero (lending disabled → waitlist) ───────────── */
function HeroWaitlist() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      await api.post("/public/waitlist", { email }).catch(() => {});
      setSent(true);
    } finally { setBusy(false); }
  };

  return (
    <section id="waitlist">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-16 pt-14 pb-16 lg:pt-20 lg:pb-24 text-center">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-[#132A13]">
          Pay school fees online.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#4F5D4F] max-w-2xl mx-auto">
          Pay directly to your child's school by card, bank transfer or USSD. School receives the
          full amount, you get an instant receipt.
        </p>
        <div className="mt-10 max-w-md mx-auto bg-white rounded-2xl border border-[#E5E3DB] p-6">
          <p className="font-display text-xl text-[#132A13] mb-2">Instalments coming soon</p>
          <p className="text-sm text-[#4F5D4F] mb-4">Join the waitlist to be told the moment monthly plans go live.</p>
          {sent ? (
            <p className="text-sm text-[#0A3622] font-medium" data-testid="waitlist-success">
              You're on the list — we'll email you.
            </p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input flex-1"
                data-testid="waitlist-email"
              />
              <button type="submit" disabled={busy} className="btn-primary justify-center" data-testid="waitlist-submit">
                Join waitlist
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* ───────────── What we do ───────────── */
function WhatWeDo() {
  return (
    <section className="py-16 lg:py-24" id="how-instalments-work">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="max-w-2xl">
          <div className="overline mb-3">What Ascentremit does</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#132A13]">
            One platform, three sides of the same problem.
          </h2>
        </div>

        <div className="grid gap-5 md:grid-cols-3 mt-10 lg:mt-14" id="for-schools">
          {SITE.LENDING_ENABLED && (
            <Card
              icon={<GraduationCap size={24} weight="duotone" />}
              title="For parents"
              body="Apply in minutes, see your exact monthly payment and total cost before you accept, and repay over 3 to 9 months. You can also pay in full."
              cta={{ to: "/parent/academic-advance", label: "Check what you can borrow" }}
            />
          )}
          <Card
            icon={<HandHeart size={24} weight="duotone" />}
            title="For schools"
            body="When a parent chooses instalments, we pay the school the whole fee upfront. The school never chases the balance and never carries the debt. Joining is free."
            cta={{ to: "/register", label: "Onboard your school" }}
          />
          <Card
            icon={<Scales size={24} weight="duotone" />}
            title="For agents"
            body={`Earn by introducing schools and families to Ascentremit. ${SITE.DSA_COMMISSION_PCT}% of what we earn, paid every month.`}
            cta={{ to: "/register", label: "Join the agent programme" }}
          />
        </div>
      </div>
    </section>
  );
}

function Card({ icon, title, body, cta }) {
  return (
    <div className="rounded-3xl bg-white border border-[#E5E3DB] p-6 sm:p-7 flex flex-col h-full">
      <div className="w-11 h-11 rounded-xl bg-[#0A3622] text-[#F2B632] grid place-items-center">{icon}</div>
      <h3 className="font-display text-xl font-medium text-[#132A13] mt-5">{title}</h3>
      <p className="text-sm text-[#4F5D4F] mt-2 leading-relaxed flex-1">{body}</p>
      {cta && (
        <Link to={cta.to} className="btn-ghost text-sm mt-5 self-start">
          {cta.label} <ArrowRight size={14} weight="bold" />
        </Link>
      )}
    </div>
  );
}

/* ───────────── How instalments are different ───────────── */
function HowItWorks() {
  if (!SITE.LENDING_ENABLED) return null;
  const steps = [
    { h: "The price is on the page.", b: "Your interest rate, monthly payment, total repayment and late fee are shown before you sign." },
    { h: "We lend only what you can repay.", b: "We check affordability before every loan. If the full term looks too much, we'll suggest a smaller amount or a longer plan rather than set you up to fail." },
    { h: "Respectful repayment.", b: "If you miss a payment, we talk to you — never your family, friends, employer or phone contacts. We never ask for access to your contacts, photos or messages." },
    { h: "Your child is never involved.", b: "The school is already paid. We never ask a school to send a pupil home or withhold results over a payment owed to us." },
    ...(!SITE.EARLY_REPAYMENT_PENALTY
      ? [{ h: "Pay off early, save on interest.", b: "Settle any time with no penalty." }]
      : []),
  ];
  return (
    <section className="py-16 lg:py-24 bg-[#F0EFEA]" id="for-agents">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="max-w-2xl mb-10 lg:mb-14">
          <div className="overline mb-3">How our instalments are different</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#132A13]">
            Fair by design, not by promise.
          </h2>
        </div>
        <ol className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {steps.map((s, i) => (
            <li
              key={s.h}
              className="rounded-2xl bg-white border border-[#E5E3DB] p-5 sm:p-6"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="font-mono text-[#F2B632] font-semibold text-sm">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-lg font-medium text-[#132A13] mt-2">{s.h}</h3>
              <p className="text-sm text-[#4F5D4F] mt-2 leading-relaxed">{s.b}</p>
            </li>
          ))}
        </ol>

        <div className="mt-10 flex items-center gap-2 text-xs text-[#4F5D4F]">
          <Lock size={14} weight="fill" className="text-[#0A3622]" />
          Card and bank payments are processed by Paystack (Nigeria) and Stripe (international).
          We never see your full card number.
        </div>
      </div>
    </section>
  );
}
